// web_components/wcRafModal/wcRafModal.ts

import { WebComponent_Base } from '../../WebComponent_Base';
import { WebComponent_Utils } from '../../WebComponent_Utils';
import { renderProductHTML } from './wcRafModal_Renderer';
import { addEventListener, removeEventListener } from '../../../utils/events';
import { applyFocusTrap } from '../../../utils/focusTrap';
import { removeSkeletonLoading } from '../../../utils/SkeletonLoading';

/**
 * A custom web component for displaying a raf modal.
 */
export class WcRafModal extends WebComponent_Base {
	public dataAttributes: any;
	public instanceId: string;
	private lastFocusedElement: Element | null = null;
	private handleKeydown!: (event: Event) => void;

	constructor() {
		super();
		this.dataAttributes = {};
		this.instanceId = WebComponent_Utils.generateUniqueId();
		this.dataAttributes = {};
		this.handleKeydown = this.handleKeydownEvent.bind(this);
	}

	/**
	 * Gets the name of the component.
	 *
	 * This getter overrides the base class implementation to return the name
	 * of the component. This value is used for logging and
	 * identifying the component in error messages or other diagnostics.
	 *
	 * @returns {string} The name of the component`.
	 */
	protected get componentName(): string {
		return 'WcRafModal';
	}

	/**
	 * Lifecycle hook called when the component is connected to the DOM.
	 */
	protected onConnected(): void {}

	/**
	 * Lifecycle hook called when the component is removed from the DOM.
	 */
	protected onDisconnected(): void {}

	/**
	 * Perform additional setup after initialization.
	 */
	protected afterInitialize() {
		removeSkeletonLoading(this);
		this.manageFocus();
	}

	/**
	 * Renders the component based on the `instanceId`.
	 * Appends the generated product content directly to the shadow DOM.
	 *
	 * @protected
	 * @returns {HTMLElement | null} A reference to the first child of the appended content in the shadow DOM,
	 *                               or `null` if no content was appended.
	 */
	protected render(): HTMLElement | null {
		// Generate the content as a string
		const renderedHTML: string = renderProductHTML(this.instanceId);

		// Convert the string to a DOM node
		const template = document.createElement('template');
		template.innerHTML = renderedHTML;

		// Append the content from the template to the shadow DOM
		this.shadowRoot!.appendChild(template.content.cloneNode(true));

		// Return the reference to the first child of the appended content
		return this.shadowRoot!.lastElementChild as HTMLElement | null;
	}

	/**
	 * Attaches listeners
	 * @private
	 * @returns {void} This method does not return a value.
	 */
	protected attachListeners(): void {
		// GTM Click Events
		WebComponent_Utils.addGTMClickListener(
			this.shadowRoot!,
			'.wc-raf__cta-link',
			'wc-raf',
			'cta raf',
		);

		const closeButton = this.shadowRoot!.querySelector('.wc-modal__close');

		if (closeButton) {
			addEventListener(closeButton, 'click', this.closeModal.bind(this));
		}

		// Add the ready event listener to apply the modal active properties
		if (document.readyState === 'loading') {
			addEventListener(
				document,
				'DOMContentLoaded',
				this.applyModalActiveProperties.bind(this),
			);
		} else {
			this.applyModalActiveProperties();
		}

		// Add keydown listener so Escape closes the modal
		addEventListener(document, 'keydown', this.handleKeydown);

		//Copy button listener
		const copyBtn = this.shadowRoot?.querySelector<HTMLAnchorElement>('.wc-raf-modal__btn');
		const rafLink = this.shadowRoot?.querySelector<HTMLParagraphElement>('.wc-raf-modal__link');

		copyBtn?.addEventListener('click', async (event) => {
			event.preventDefault();

			const text = rafLink?.textContent?.replace(/\s+/g, '');

			if (!text) return;

			try {
				await navigator.clipboard.writeText(`https://${text}`);

				copyBtn.textContent = 'Copied';
			} catch {
				copyBtn.textContent = 'Failed to copy';
			}

			setTimeout(() => {
				copyBtn.textContent = 'Copy';
			}, 2000);
		});
	}

	/**
	 * Applies the necessary classes to the body and modal.
	 * We are applying a class to both body and the modal itself, rather than just the body,
	 * since we need to adjust the body itself outside the shadowDOM, while also initialising
	 * the modal held in the shadowDOM.
	 */
	applyModalActiveProperties() {
		const body = document.querySelector('body') as HTMLElement;
		const rafModal = this.shadowRoot!.querySelector('.wc-raf-modal') as HTMLElement;
		if (!body || !rafModal) return;

		setTimeout(function () {
			if (!body.classList.contains('modal-active')) {
				body.classList.add('modal-active');
			}
			rafModal?.classList.add('modal-active', 'modal-transition');
		}, 500);

		rafModal.addEventListener(
			'transitionend',
			() => {
				// Shouldn't need a timeout, but I think there was an issue with the web component
				// loading with the transition simultaneously and this transitionend firing prematurely
				setTimeout(function () {
					rafModal?.classList.remove('modal-transition');
				}, 500);
			},
			{ once: true },
		);
	}

	/**
	 * Handles the 'keydown' event to listen for the "Escape" key.
	 * When the "Escape" key is pressed, it invokes the acceptAllCookies function.
	 *
	 * @param {Event} event - The event object, cast as a KeyboardEvent to access key properties.
	 */
	handleKeydownEvent(event: Event) {
		if ((event as KeyboardEvent).key === 'Escape') {
			event.stopPropagation();
			this.closeModal();
		}
	}

	/**
	 * Closes the modal and restores focus to the last focused element.
	 * This method ensures that when the modal is dismissed, focus is returned to the
	 * previously focused element to maintain a logical focus flow.
	 * Also removes the keydown listener to prevent memory leaks.
	 */
	closeModal() {
		this.remove();
		const body = document.querySelector('body') as HTMLElement;
		body.classList.remove('modal-active');
		if (this.lastFocusedElement) {
			(this.lastFocusedElement as HTMLElement).focus();
		}

		// Remove the keydown event listener when the modal is closed
		removeEventListener(document, 'keydown', this.handleKeydown);
	}

	/**
	 * Manages focus within the modal to ensure accessibility.
	 * Uses `requestAnimationFrame` to delay focus setting until after the browser has had a chance
	 * to render any updates, preventing potential layout thrashing.
	 */
	manageFocus() {
		this.lastFocusedElement = document.activeElement;
		const modal = this.shadowRoot!.getElementById('wc-raf-modal') as HTMLElement;
		if (modal) {
			//modal.focus(); // Set focus to the modal when it appears
			applyFocusTrap(modal); // Use the utility function to apply focus trapping
		}
	}

	/**
	 * Applies visibility settings to all elements within the shadow DOM based content.
	 * This ensures elements are shown or hidden according to the presence of variable data
	 * and the component settings file.
	 */
	protected conditionalVisibility(): void {
		WebComponent_Utils.applyVisibilitySettings(<ShadowRoot>this.shadowRoot);
	}
}

/**
 * Registers the custom element:
 * - Checks if the component has already been defined to avoid registration errors
 * - Registers the component with the browser’s CustomElementRegistry
 * - Automatically re-registers the component if the page is restored from Safari’s back-forward cache (bfcache)
 */
WebComponent_Utils.defineComponent('wc-raf-modal', WcRafModal);
